/**
 * LifecycleTab — the "Ownership Lifecycle" tab of the Accountability Registry App Pack.
 *
 * The live state machine over the ownership records: creation-gate → DISCOVERED_UNOWNED → (claim)
 * → NOMINATED → (attest) → ACTIVE, with the orphan → suspend → re-assign remediation branch. Every
 * count and row is real, RBAC-scoped registry state (getOwnershipRollup + listAccountabilityRegistry) —
 * no fabricated data. Attention states (unowned, awaiting acceptance, orphaned) list their agents with
 * the remediation each needs, deep-linking to the agent.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';
import ScopeNote from './ScopeNote';
import { Card, Pill, Button, EmptyState, Skeleton } from '../../components/v2';
import { StateNode, FlowArrow, FlowRow, FlowPanel, SectionHead, Callout } from './AccountabilityUI';
import { GitBranch, AlertTriangle, ShieldCheck, UserPlus, HelpCircle } from 'lucide-react';

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);


// Attention states that need a human action, with the remediation label.
const ATTENTION = [
  { state: 'DISCOVERED_UNOWNED', label: 'Unowned — nominate an owner', tone: 'danger', action: 'Nominate owner', icon: <UserPlus size={14} /> },
  { state: 'NOMINATED', label: 'Awaiting owner acceptance', tone: 'warning', action: 'Accept Accountability', icon: <ShieldCheck size={14} /> },
  { state: 'ORPHANED', label: 'Orphaned — owner no longer exists (fail-closed)', tone: 'danger', action: 'Re-assign', icon: <AlertTriangle size={14} /> },
];

function toneText(t) {
  return { danger: 'text-v2-danger', warning: 'text-v2-warning', success: 'text-v2-success', muted: 'text-v2-ink-muted' }[t] || 'text-v2-ink';
}

export default function LifecycleTab({ active = true, customerId = null }) {
  const [scope, setScope] = useState(null);
  const navigate = useNavigate();
  const [byState, setByState] = useState(null);
  const [rows, setRows] = useState({});     // state -> agent rows (attention states)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Entry-based lifecycle counts (1-per-agent projection), so the distribution matches the
      // Registry — the record ledger (getOwnershipRollup) double-counts generations and retains
      // retired records of fully-deleted agents, which is confusing here.
      const posture = unwrap(await api.getOwnershipPosture(customerId));
      const bs = posture?.inventory?.byState || {};
      setByState(bs);
      setScope(posture?.scope || null);
      // For each attention state that has records, pull a short list (reuse the registry list).
      const next = {};
      await Promise.all(ATTENTION.map(async (a) => {
        if ((bs[a.state] || 0) > 0) {
          try {
            const page = unwrap(await api.listAccountabilityRegistry({ state: a.state, page: 0, size: 5 }));
            next[a.state] = Array.isArray(page?.content) ? page.content : [];
          } catch (e) { next[a.state] = []; }
        }
      }));
      setRows(next);
    } catch (e) {
      setError(e?.message || 'Could not load the ownership lifecycle.');
      setByState(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // customerId is a real dependency: without it the memoised loader keeps the customer that
  // was current when it was created, and switching customers would silently show stale scope.
  useEffect(() => { load(); }, [load]);

  // Background refresh on re-activation (keep-alive keeps this tab mounted).
  const reactivatedRef = React.useRef(false);
  useEffect(() => {
    if (!active) return;
    if (!reactivatedRef.current) { reactivatedRef.current = true; return; }
    load();
  }, [active]);

  const total = useMemo(() => {
    if (!byState) return 0;
    return Object.values(byState).reduce((a, b) => a + (b || 0), 0);
  }, [byState]);

  if (loading && !byState) return <div className="p-2"><Skeleton variant="text" lines={10} /></div>;
  if (error) {
    return (
      <EmptyState icon={<HelpCircle size={22} />} title="Could not load Ownership Lifecycle" description={error}
        primaryAction={<Button variant="secondary" size="sm" onClick={load}>Try again</Button>} />
    );
  }
  const bs = byState || {};

  return (
    <div className="space-y-5">
      <ScopeNote scope={scope} />
      {/* Real-data callout when an owner has gone non-existent */}
      {(bs.ORPHANED || 0) > 0 ? (
        <Callout tone="danger">
          <b className="text-v2-ink">{bs.ORPHANED} record{(bs.ORPHANED) === 1 ? '' : 's'} orphaned — owner no longer exists (fail-closed).</b>{' '}
          The prior owner was deprovisioned (IGA authoritative). Re-assignment via manager + attestation is required; there is no admin override.
        </Callout>
      ) : null}

      {/* State machine — mockup labeled-flow layout, real states + counts */}
      <Card padding="md">
        <div className="flex items-center gap-2 mb-1">
          <GitBranch size={16} className="text-v2-trust" />
          <SectionHead title="State machine" sub={`${total} agent${total === 1 ? '' : 's'} in scope · creation gate is deny-by-default`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <FlowPanel title="Entry · protected creation">
            <FlowRow className="mb-0">
              <StateNode variant="gate">CREATION GATE G1–G8</StateNode>
              <FlowArrow />
              <StateNode variant="ok" count={bs.NOMINATED || 0}>NOMINATED</StateNode>
              <FlowArrow label="owner attests" />
              <StateNode variant="ok" count={bs.ACTIVE || 0}>ACTIVE</StateNode>
            </FlowRow>
          </FlowPanel>

          <FlowPanel title="Entry · discovered">
            <FlowRow className="mb-0">
              <StateNode variant="gate" count={bs.DISCOVERED_UNOWNED || 0}>DISCOVERED_UNOWNED</StateNode>
              <FlowArrow label="claim → nominate → attest" />
              <StateNode>ACTIVE</StateNode>
            </FlowRow>
          </FlowPanel>

          <FlowPanel title="Owner no longer exists">
            <FlowRow>
              <StateNode variant="ok">ACTIVE</StateNode>
              <FlowArrow label="IGA leaver" />
              <StateNode variant="bad" count={bs.ORPHANED || 0}>ORPHANED (fail-closed)</StateNode>
              <FlowArrow label="re-assign + attest" />
              <StateNode>ACTIVE (recovered)</StateNode>
            </FlowRow>
            <FlowRow className="mb-0">
              <FlowArrow label="escalation exhausted" />
              <StateNode variant="dash" count={bs.RETIRED || 0}>RETIRED (sealed)</StateNode>
            </FlowRow>
          </FlowPanel>
        </div>
      </Card>

      {/*
        * The states the product can actually be in.
        *
        * DELEGATED, TRANSFER_PENDING and SUCCESSION_PENDING are declared in
        * OwnershipStateMachine (:49-51, :55-61) and nothing calls
        * GRANT_DELEGATION, INITIATE_TRANSFER or INITIATE_SUCCESSION — a
        * whole-repo grep finds those transitions only in the table, the enum
        * and a unit test. Drawing them put four unbuilt capabilities on screen
        * as shipped accountability, with counts structurally pinned at zero
        * that read as "none in flight" rather than "does not exist".
        *
        * They stay in the enum, because the model is right. They come off the
        * screen until something can enter them.
        */}
      <Card padding="sm">
        <div className="text-[12px] font-semibold text-v2-ink-muted mb-2">Full lifecycle distribution</div>
        <div className="flex flex-wrap gap-2">
          {['DISCOVERED_UNOWNED', 'NOMINATED', 'ACTIVE', 'ORPHANED', 'RETIRED'].map((st) => (
            <Pill key={st} tone={(bs[st] || 0) > 0 ? 'brand' : 'muted'} size="sm">
              {st.replace(/_/g, ' ')} · {bs[st] || 0}
            </Pill>
          ))}
        </div>
      </Card>

      {/* Attention states — remediation loop */}
      {ATTENTION.filter((a) => (bs[a.state] || 0) > 0).length === 0 ? (
        <Card padding="none">
          <EmptyState icon={<ShieldCheck size={22} />} title="No records need attention"
            description="Every agent has an accountable owner and current attestation." />
        </Card>
      ) : (
        ATTENTION.filter((a) => (bs[a.state] || 0) > 0).map((a) => (
          <Card key={a.state} padding="md">
            <div className="flex items-center gap-2 mb-2">
              <span className={toneText(a.tone)}>{a.icon}</span>
              <h4 className="text-[13px] font-semibold text-v2-ink">{a.label}</h4>
              <Pill tone={a.tone} size="sm">{bs[a.state] || 0}</Pill>
            </div>
            <div className="divide-y divide-v2-border border border-v2-border rounded-v2-md">
              {(rows[a.state] || []).map((r) => (
                <div key={r.entryId || r.agentId} className="flex items-center gap-2 px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-v2-ink truncate">{r.agentName || 'Unnamed agent'}</div>
                    {/* Short agent id disambiguates same-named agents (real distinct systems). */}
                    <div className="text-[11px] text-v2-ink-muted font-mono truncate">{(r.agentId || '').slice(0, 8)}</div>
                  </div>
                  {r.materialityTier ? <Pill tone="muted" size="sm">{r.materialityTier.replace('TIER_', 'T')}</Pill> : null}
                  <Button variant="secondary" size="sm" iconLeft={a.icon}
                    onClick={() => navigate('/accountability?tab=outcomes')}>
                    Remediate
                  </Button>
                </div>
              ))}
              {(bs[a.state] || 0) > (rows[a.state] || []).length ? (
                <div className="px-3 py-2 text-[12px] text-v2-ink-muted">
                  +{(bs[a.state] || 0) - (rows[a.state] || []).length} more — see Outcomes &amp; Actions to remediate.
                </div>
              ) : null}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
